/**
 * IPhysicsVehicle - interface for physics vehicle controllers. Encapsulates vehicle-specific
 * physics operations including wheel management, steering, engine/brake forces and state queries.
 */

import type * as THREE from 'three';

/**
 * Configuration for a vehicle wheel
 */
export interface VehicleWheelConfig {
  /** Position relative to vehicle chassis center */
  position: THREE.Vector3;
  /** Wheel radius in meters */
  radius: number;
  /** Wheel width in meters */
  width: number;
  /** Whether this wheel can steer */
  canSteer: boolean;
  /** Whether this wheel receives engine power */
  isPowered: boolean;
  /** Whether this wheel can brake */
  canBrake: boolean;
  /** Suspension rest length in meters */
  suspensionRestLength: number;
  /** suspension_stiffness (default: 5.88) - How stiff the suspension springs are. Higher = less compression, stiffer ride */
  suspensionStiffness?: number;
  /** suspension_compression (default: 0.83) - Damping during compression. Higher = less bouncing when hitting bumps */
  suspensionCompression?: number;
  /** suspension_damping (default: 0.88) - Damping during extension. Higher = prevents overshoot/oscillation */
  suspensionRelaxation?: number;
  /** max_suspension_force (default: 6000.0) - Maximum force the suspension can apply */
  maxSuspensionForce?: number;
  /** max_suspension_travel (default: 5.0) - Maximum distance suspension can compress/extend from rest position */
  maxSuspensionTravel?: number;
  /** side_friction_stiffness (default: 1.0) - Lateral tire grip multiplier for cornering */
  sideFrictionStiffness?: number;
  /** friction_slip (default: 10.5) - Tire traction amount. Higher = more grip, more instantaneous braking (risk of flipping) */
  frictionSlip?: number;
  /** Wheel direction vector (usually pointing down) */
  wheelDirection?: THREE.Vector3;
  /** Wheel axle vector (usually pointing left/right) */
  wheelAxle?: THREE.Vector3;
}

/**
 * Transform information for a vehicle wheel
 */
export interface VehicleWheelState {
  /** Wheel position relative to the chassis */
  relativePosition: THREE.Vector3;
  /** Wheel rotation quaternion relative to the chassis */
  relativeQuaternion: THREE.Quaternion;
  /** Current steering angle in radians */
  steeringAngle: number;
  /** Current wheel rotation angle in radians */
  rotationAngle: number;

  /** Suspension rest length in meters */
  suspensionRestLength: number | null;
  /** Maximum suspension travel */
  maxSuspensionTravel: number | null;
  /** Wheel radius in meters */
  radius: number | null;
  /** Suspension stiffness (higher = stiffer) */
  suspensionStiffness: number | null;
  /** Suspension compression coefficient */
  suspensionCompression: number | null;
  /** Suspension relaxation coefficient */
  suspensionRelaxation: number | null;
  /** Maximum suspension force */
  maxSuspensionForce: number | null;
  /** Current brake force */
  brakeForce: number | null;
  /** Current engine force */
  engineForce: number | null;
  /** Wheel friction coefficient */
  frictionSlip: number | null;
  /** Side friction stiffness */
  sideFrictionStiffness: number | null;
  /** Current suspension force */
  suspensionForce: number | null;
  /** Contact normal */
  contactNormal: THREE.Vector3 | null;
  /** Contact point */
  contactPoint: THREE.Vector3 | null;
  /** Current suspension length */
  suspensionLength: number | null;
  /** Hard point */
  hardPoint: THREE.Vector3 | null;
  /** Whether the wheel is touching the ground */
  isInContact: boolean | null;
}

/**
 * State information for a vehicle
 */
export interface VehicleState {
  /** Current linear velocity */
  velocity: THREE.Vector3;
  /** Current angular velocity */
  angularVelocity: THREE.Vector3;
  /** Current speed in m/s */
  speed: number;
  /** Number of wheels touching the ground */
  wheelsOnGround: number;
  /** Total number of wheels */
  totalWheels: number;
}

export interface IPhysicsVehicle {
  /**
   * Add a wheel to the vehicle.
   * @param wheelConfig - Configuration for the wheel
   * @returns The wheel index, or -1 if failed
   */
  addWheel(wheelConfig: VehicleWheelConfig): number;

  /**
   * Set engine force for specified wheels.
   * @param force - Engine force to apply
   */
  setEngineForce(force: number): void;

  /**
   * Set brake force for specified wheels.
   * @param force - Brake force to apply
   */
  setBrakeForce(force: number): void;

  /**
   * Set steering angle for specified wheels.
   * @param angle - Steering angle in radians
   */
  setSteering(angle: number): void;

  /**
   * Get the transform (position and rotation) of a specific wheel.
   * @param wheelIndex - Index of the wheel
   * @returns Transform data or null if invalid wheel index
   */
  getWheelState(wheelIndex: number): VehicleWheelState | null;

  /**
   * Get the current state of the vehicle (speed, RPM, etc.).
   * @returns Current vehicle state
   */
  getState(): VehicleState | null;

  /**
   * Get the number of wheels on the vehicle.
   * @returns Number of wheels
   */
  numWheels(): number;

  /**
   * Destroy the vehicle.
   */
  destroy(): void;
}
